// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — MineriumScreen.jsx   ·   THE HOLDING, PLAYED
// ────────────────────────────────────────────────────────────────────
// The screen that owns the movement loop, the HUD, build mode, the
// harvest, the shortcut to Bonnie's Basket and the door back into the Hall.
// `Minerium3D.jsx` draws; this decides.
//
// ── ⚠ MOVEMENT NEVER RE-RENDERS ────────────────────────────────────
// `posRef` is the SOURCE OF TRUTH for position and `pos` state is a
// throttled MIRROR of it. That inversion is the entire reason a 3D
// screen is smooth: movement flows through refs into useFrame and React
// hears about it a few times a second instead of every step.
// (ie-realtime-perf Cause 1. The pier learned this the hard way and
// this screen is built on the far side of that lesson.)
//
// ── ⚠ CLEAR KEYS BY MUTATION, NEVER BY REPLACEMENT ─────────────────
// The key listeners and the movement loop share ONE object. Replacing
// it orphans whichever side captured the old one, and it breaks in
// total silence — no error, no warning, no console line.
// (ie-realtime-perf Cause 3b.)
// ════════════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Minerium3D, { HOUSE_DOOR } from "./Minerium3D";
import {
  YARD, inYard, snap, bindServerClock, serverNow, reachTile, ghostStateFor, blockedByProp, nextAim, yardFloorOk,
  dimsOf, quarterRot, rotStepFor,
  listenToHolding, emptyHolding, resolvePlacement, shedFor, propsInArea,
  growthOf, pendingTotal, harvest, harvestAll,
  placeProp, moveProp, rotateProp, pickUpProp, setCompanion,
} from "./minerium";
import {
  ITEMS, KIND, AREAS, SHELF_META, LAYER, layerOf, turnsInQuarters,
} from "./mineriumCatalogue";
import {
  listenToStock, listenToOwned, foundHolding,
  wearItem, stockOf,
  // ⭐ THE TESTING TILL (L52 · OW-21). `priceOf` is what you PAY,
  // `listPriceOf` is the sticker. This screen reads BOTH and shows both,
  // and it must never read `item.priceUD` directly again — that field is
  // the sticker, and rendering it as the price is precisely the bug this
  // pair exists to make impossible.
  // (buying moved to Bonnie's Basket — BonnieMinerium.jsx — 2026-09-25)
  priceOf, listPriceOf,
} from "./mineriumShop";
import { listenToDawgs, UD_ROW } from "./upDawgs";
import { COMPANIONS, companionCard, companionUnlocked } from "./mineriumCompanion";
import { rotateInput, resetGroundForward } from "./pierCamera";
import { UI, uiPanel, uiBtn, uiLabel } from "./theme";
import { ref, set, update, remove, onValue } from "firebase/database";
import { db } from "./firebase";
import { MobileDpad, isMobile } from "./PlayerEntity";
// ⭐ (2026-09-15) the build chrome both rooms share — the shed with
// pictures, the first-visit card, undo, the +✹ bursts, the sounds
import { isTypingTarget, GHOST_VERB, useUndo, useBursts, Thumb, ShedPanel, OnboardCard, ABOVE_SHED_MOBILE } from "./buildKit";
import { sfx } from "./pierSfx";
import PierMarketplace from "./PierMarketplace";

const UD = "✹";
const fmt = (n) => (n || 0).toLocaleString();

// ════════════════════════════════════════════════════════════════════
// CHROME — one place, so nothing in this screen invents its own look
// ════════════════════════════════════════════════════════════════════
// ⚠⚠ THIS SCREEN USED TO BE A DIFFERENT GAME. It was the only room in
// the Empire that imported nothing from `theme.js`, and it had invented
// its own: Courier New, an olive-black panel (`rgba(8,10,6,.93)`), a
// moss border (`#2c3a22`), 5px and 3px radii, `#d8e8c8` pale-green body
// text and em-based tracking — eleven divergences from the Hall you
// reach it through, none of them wrong alone and all of them together
// making the chrome change species when you walk out of your own door.
//
// ⚑ The GREEN STAYS, but only where it means something: it is the Dawgs
// tint (`UD_ROW.tint`), so it marks a currency, not a room.
// ⚑ `#5e6a52` was used fourteen times as "dim" at about 3.4:1 — under
// the readable floor for real copy. `UI.dim` is 5.8:1 on this panel.
const MONO = UI.font;                 // the pier's face, not a terminal's
const GREEN = UI.ok;
// (the shop's kind words and filter chips moved with the counter → BonnieMinerium.jsx)
const INK = "#0d1408";

const panel = {
  ...uiPanel(),
  position: "absolute", zIndex: 600,
  backdropFilter: "blur(8px)",
};

const btn = (on, tint = UI.gold) => uiBtn(tint, { on });

const solidBtn = (tint = UI.gold, disabled = false) => ({
  fontFamily: UI.font, fontSize: UI.size.label, letterSpacing: UI.track.label, fontWeight: 700,
  padding: "8px 14px", borderRadius: UI.rMd, border: "none",
  cursor: disabled ? "not-allowed" : "pointer",
  background: disabled ? UI.edge : tint,
  color: disabled ? UI.dim : INK,
});

const cap = { ...uiLabel };

// ════════════════════════════════════════════════════════════════════
// THE SCREEN
// ════════════════════════════════════════════════════════════════════
// presence in the yard — the Hall's numbers, so a ghost times out the same
const YARD_GHOST_TTL = 15000, YARD_HEARTBEAT_MS = 5000, YARD_SYNC_MS = 90, YARD_CAP = 6;

export default function MineriumScreen({
  ownerUid, ownerName = "", viewerUid, viewerName = "", profile, onEnterHall, onLeave,
}) {
  const isOwner = ownerUid === viewerUid;
  const gender = profile?.gender || "f";

  // ── live state ────────────────────────────────────────────────────
  const [holding, setHolding] = useState(emptyHolding);
  const [stock, setStock]     = useState({});
  const [owned, setOwned]     = useState({});
  const [dawgs, setDawgs]     = useState(0);

  const [buildMode, setBuildMode] = useState(false);
  const [ghostItemId, setGhost]   = useState(null);
  const [ghostRot, setGhostRot]   = useState(0);
  const [movingPropId, setMoving] = useState(null);
  const movingRef = useRef(null); movingRef.current = movingPropId;
  // ⭐ (2026-09-15) a piece you CLICKED, as opposed to the nearest one.
  // Click-to-select is how every building game picks a thing; proximity
  // is the keyboard/D-pad fallback and it stays.
  const [pickedProp, setPicked]   = useState(null);
  // ⭐ where a tap on the ground asked the ghost to go. Walking clears it.
  const [aim, setAim]             = useState(null);
  const [overlay, setOverlay]     = useState(null);   // "shop" | "companion" | "sky" | null
  const [flash, setFlash]         = useState(null);
  const [busy, setBusy]           = useState(false);
  const undo = useUndo();
  const { bursts, burst } = useBursts();

  // ⚠ posRef is the truth; `pos` is a 15Hz mirror React is allowed to see.
  const posRef    = useRef({ ...YARD.spawn });
  const facingRef = useRef({ x: 0, y: -1 });
  const aimRef    = useRef(null);
  aimRef.current = aim;
  // The 15Hz mirror. ⚠ The VALUE is used, not just the setter: the
  // ghost's ring is derived from it, so the ring is accurate as you walk
  // instead of only when the nearest prop changes.
  // ⚠ AND THE FACING IS MIRRORED TOO (2026-09-15). Pinned against the
  // fence and pressing D turns you east without moving you, so the ghost
  // moved and the ring — keyed on position only — stayed green over a
  // spot that was through the fence.
  const [posMirror, setPosMirror] = useState({ ...YARD.spawn });
  const [facingMirror, setFacingMirror] = useState({ x: 0, y: -1 });
  const [nearProp, setNearProp] = useState(null);
  const [nearDoor, setNearDoor] = useState(false);
  const keysRef = useRef({});
  const rafRef  = useRef(null);
  const holdingRef = useRef(holding);
  holdingRef.current = holding;
  const barRef = useRef(null);
  const [barH, setBarH] = useState(52);
  // the companion: where it is (written by the renderer), and the pet counter
  const companionPosRef = useRef({ x: -99, y: -99 });
  const petRef = useRef(0);
  const [nearCompanion, setNearCompanion] = useState(false);
  // ⭐ THE OTHERS (2026-09-15) — same node as the Hall, `room: "yard"`
  const [others, setOthers] = useState({});
  const othersRef = useRef(others); othersRef.current = others;
  const rosterKey = useMemo(() => Object.entries(others).map(([u, d]) => `${u}:${d?.name || ""}:${d?.gender || ""}`).sort().join(","), [others]);

  const say = useCallback((ok, text) => {
    setFlash({ ok, text });
    window.clearTimeout(say._t);
    say._t = window.setTimeout(() => setFlash(null), 3800);
    if (!ok) sfx.deny();
  }, []);

  // ── the server clock, before anything reads a timer ───────────────
  useEffect(() => bindServerClock(), []);

  // ── subscriptions ─────────────────────────────────────────────────
  useEffect(() => listenToHolding(ownerUid, setHolding), [ownerUid]);
  useEffect(() => (isOwner ? listenToStock(viewerUid, setStock) : undefined), [isOwner, viewerUid]);
  useEffect(() => (isOwner ? listenToOwned(viewerUid, setOwned) : undefined), [isOwner, viewerUid]);
  useEffect(() => (isOwner ? listenToDawgs(viewerUid, setDawgs) : undefined), [isOwner, viewerUid]);

  // ── the founding grant, once ──────────────────────────────────────
  // Fires on the owner's first ever visit and is inert forever after.
  // It never reads a balance, so it cannot fail on funds. (Concurrent
  // calls — StrictMode's double mount — share one promise, see the shop.)
  useEffect(() => {
    if (!isOwner || !viewerUid) return;
    let alive = true;
    (async () => {
      const r = await foundHolding(viewerUid);
      if (alive && r.ok) {
        setBuildMode(true);
        say(true, `W'ily left you a founding crate, a purse of ${UD}${fmt(r.purseUD || 0)}, and somebody to keep you company.`);
      }
    })();
    return () => { alive = false; };
  }, [isOwner, viewerUid, say]);

  // ── ⚠ reset the shared forward on the way out ─────────────────────
  useEffect(() => () => resetGroundForward(), []);

  // ── presence: instanced by ownerUid, filtered to this room ────────
  useEffect(() => {
    if (!ownerUid || !viewerUid) return undefined;
    const basePath = `homes/${ownerUid}/players`;
    const myRef = ref(db, `${basePath}/${viewerUid}`);
    const p = posRef.current;
    set(myRef, { name: viewerName || "AENARCHIST", x: p.x, y: p.y, facingX: 0, facingY: -1, gender, room: "yard", ts: Date.now() }).catch(() => {});
    const unsub = onValue(ref(db, basePath), (snap) => {
      const all = snap.val() || {};
      const cutoff = Date.now() - YARD_GHOST_TTL;
      const out = {};
      for (const [uid, d] of Object.entries(all)) {
        if (uid === viewerUid || !d) continue;
        if (!d.ts || d.ts < cutoff) { remove(ref(db, `${basePath}/${uid}`)).catch(() => {}); continue; }
        if (d.room !== "yard") continue;                 // they are indoors
        if (Object.keys(out).length < YARD_CAP) out[uid] = d;
      }
      setOthers(out);
    });
    const heartbeat = setInterval(() => { update(myRef, { ts: Date.now() }).catch(() => {}); }, YARD_HEARTBEAT_MS);
    return () => { unsub(); clearInterval(heartbeat); remove(myRef).catch(() => {}); };
  }, [ownerUid, viewerUid, viewerName, gender]);
  const lastSync = useRef(0);
  const syncPos = useCallback((x, y, fx, fy) => {
    if (!ownerUid || !viewerUid) return;
    const now = Date.now();
    if (now - lastSync.current < YARD_SYNC_MS) return;
    lastSync.current = now;
    update(ref(db, `homes/${ownerUid}/players/${viewerUid}`), { x, y, facingX: fx, facingY: fy, ts: now }).catch(() => {});
  }, [ownerUid, viewerUid]);
  const syncRef = useRef(syncPos); syncRef.current = syncPos;

  // the bar's real height, so the flash and the prompt clear it on a phone
  useEffect(() => {
    const el = barRef.current; if (!el) return undefined;
    const ro = new ResizeObserver(() => setBarH(el.getBoundingClientRect().height));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── keys ──────────────────────────────────────────────────────────
  // ⚠ Never while typing (the social dock's friend-code box is live in
  // this screen), and the keys CLEAR ON BLUR — alt-tab with W held used
  // to walk you into the north fence until you came back and tapped W.
  useEffect(() => {
    const down = (e) => {
      if (isTypingTarget(e)) return;
      keysRef.current[e.key] = true;
      if (e.key === "Escape") { setOverlay(null); setGhost(null); setMoving(null); setPicked(null); setAim(null); }
    };
    const up = (e) => { keysRef.current[e.key] = false; };
    const clear = () => { const k = keysRef.current; for (const kk in k) k[kk] = false; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", clear);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", clear);
    };
  }, []);

  // ════════════════════════════════════════════════════════════════
  // ⭐ THE MOVEMENT LOOP — the pier's analog integrator, unchanged in
  // feel. SPEED_TPS is derived from the same 150ms-per-tile number the
  // Empire has always walked at, so a player crossing their own yard
  // moves at exactly the speed they cross the boardwalk.
  // ════════════════════════════════════════════════════════════════
  useEffect(() => {
    const menuOpen = !!overlay;
    const clearKeys = () => { const k = keysRef.current; for (const kk in k) k[kk] = false; };
    if (menuOpen) { clearKeys(); return undefined; }

    const SPEED_TPS = 1000 / 150;
    const STATE_HZ  = 15;
    const NEAR_HZ   = 7;
    let last = 0, tState = 0, tNear = 0;

    const octant = (dx, dy) => (Math.abs(dx) > Math.abs(dy)
      ? { x: dx > 0 ? 1 : -1, y: 0 }
      : { x: 0, y: dy > 0 ? 1 : -1 });

    // the ground you may stand on: inside the fence and not inside a
    // solid piece of furniture (rugs, paths, beds and doors are walk-over).
    // ⚠ A piece you are already INSIDE (you spawned in it, it was placed
    // around you) never blocks a step — so nobody is welded to the porch
    // (review, 2026-09-15). The piece being moved is not there either.
    const free = (x, y) => {
      if (!inYard(x, y)) return false;
      const props = holdingRef.current.props;
      const b = blockedByProp(props, AREAS.YARD, x, y, 0.3, movingRef.current);
      if (!b) return true;
      const here = posRef.current;
      return blockedByProp(props, AREAS.YARD, here.x, here.y, 0.3, movingRef.current)?.propId === b.propId;
    };

    function loop(ts) {
      if (!last) last = ts;
      const dt = Math.min(0.05, (ts - last) / 1000);   // clamp a tab-switch
      last = ts;

      const k = keysRef.current;
      let ix = 0, iy = 0;
      if (k.ArrowLeft  || k.a || k.A) ix = -1;
      if (k.ArrowRight || k.d || k.D) ix =  1;
      if (k.ArrowUp    || k.w || k.W) iy = -1;
      if (k.ArrowDown  || k.s || k.S) iy =  1;

      // ⭐ CAMERA-RELATIVE. W means "away from the camera", always.
      const dir = rotateInput(ix, iy);

      if (dir.x || dir.y) {
        const p = posRef.current;
        const st = SPEED_TPS * dt;
        let nx = p.x + dir.x * st;
        let ny = p.y + dir.y * st;
        // WALL SLIDE — a blocked diagonal keeps the free axis rather
        // than dead-stopping you against the fence or a pillar.
        if (!free(nx, ny)) {
          if (free(nx, p.y))      ny = p.y;
          else if (free(p.x, ny)) nx = p.x;
          else { nx = p.x; ny = p.y; }
        }
        posRef.current = { x: nx, y: ny };
        const f = octant(dir.x, dir.y);
        if (f.x !== facingRef.current.x || f.y !== facingRef.current.y) facingRef.current = f;
        syncRef.current(nx, ny, f.x, f.y);
        // walking takes the aim back — the ghost follows you again
        if (aimRef.current) { aimRef.current = null; setAim(null); }
      }

      const P = posRef.current;
      if (ts - tState > 1000 / STATE_HZ) {
        tState = ts;
        setPosMirror((prev) => (prev.x === P.x && prev.y === P.y ? prev : { ...P }));
        const F = facingRef.current;
        setFacingMirror((prev) => (prev.x === F.x && prev.y === F.y ? prev : { ...F }));
      }
      if (ts - tNear > 1000 / NEAR_HZ) {
        tNear = ts;
        const h = holdingRef.current;
        let best = null, bestD = 1.5;
        // ⚠⚠ THE YARD ONLY (see the 2026-09-13 note: an unfiltered sweep
        // reached into the Hall). ⚑ Ties go to the TOP of a stack — the
        // torch and the barrel it stands on resolve to the same spot, and
        // the base always won by insertion order, so a stacked piece could
        // never be selected at all (2026-09-15).
        // ⭐ OW-34: distance to the piece's EDGE (a 3-long partition is
        // reachable from its end, not only its middle), plus a hair for
        // whatever stands above a rug, so the rug under a chair is never
        // the one your hand finds first
        for (const p of propsInArea(h.props, AREAS.YARD)) {
          const at = resolvePlacement(h.props, p.propId);
          if (!at) continue;
          const { w, d: dd } = dimsOf(p.itemId, at.rot);
          const ex = Math.max(0, w / 2 - 0.5), ey = Math.max(0, dd / 2 - 0.5);
          const qx = Math.max(0, Math.abs(at.x - P.x) - ex), qy = Math.max(0, Math.abs(at.y - P.y) - ey);
          const d = Math.hypot(qx, qy) - (p.on ? 0.002 : 0) + (layerOf(p.itemId) === LAYER.FLOOR ? 0.001 : 0);
          if (d < bestD) { bestD = d; best = p.propId; }
        }
        setNearProp((cur) => (cur === best ? cur : best));
        const doorD = Math.hypot(P.x - HOUSE_DOOR.x, P.y - HOUSE_DOOR.y) < 2.0;
        setNearDoor((cur) => (cur === doorD ? cur : doorD));
        const c = companionPosRef.current;
        const nearC = Math.hypot(P.x - c.x, P.y - c.y) < 1.4;
        setNearCompanion((cur) => (cur === nearC ? cur : nearC));
      }
      rafRef.current = requestAnimationFrame(loop);
    }
    rafRef.current = requestAnimationFrame(loop);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [overlay]);

  // a clicked piece stays selected until you walk away from it
  useEffect(() => {
    if (!pickedProp) return;
    const p = holding.props?.[pickedProp];
    if (!p) { setPicked(null); return; }
    const at = resolvePlacement(holding.props, pickedProp) || p;
    if (Math.hypot(at.x - posMirror.x, at.y - posMirror.y) > 3.2) setPicked(null);
  }, [pickedProp, posMirror, holding.props]);

  // ── the piece under your hand, and where a held piece lands ──────
  const activeProp = pickedProp || nearProp;
  const activeGhostItem = movingPropId
    ? holding.props?.[movingPropId]?.itemId
    : ghostItemId;
  // ⭐ OW-34: a non-square piece only ever shows (and lands) on a quarter
  const effRot = activeGhostItem && turnsInQuarters(activeGhostItem) ? quarterRot(ghostRot) : ghostRot;
  const ghostFp = useMemo(() => (activeGhostItem ? dimsOf(activeGhostItem, effRot) : 1), [activeGhostItem, effRot]);

  // ⭐ THE ONE REACH (minerium.js `reachTile`), or the tile you tapped.
  const ghostAt = useMemo(
    () => (aim ? { x: snap(aim.x), y: snap(aim.y) } : reachTile(AREAS.YARD, posMirror, facingMirror, ghostFp)),
    [aim, posMirror, facingMirror, ghostFp],
  );

  // ⭐ THREE ANSWERS, from the engine, so the ring, the prompt and E
  // cannot disagree (2026-09-15). Recomputed on the 15Hz mirror.
  const ghost = useMemo(() => {
    if (!activeGhostItem) return null;
    return ghostStateFor(holding.props, AREAS.YARD, ghostAt.x, ghostAt.y, activeGhostItem, { ignorePropId: movingPropId, floorOk: ITEMS[activeGhostItem]?.walkOver ? null : yardFloorOk, rot: effRot });
  }, [activeGhostItem, holding.props, movingPropId, ghostAt, effRot]);
  const ghostBlocked = ghost?.state === "blocked";

  // the renderer reads these by ref inside useFrame
  const ghostAtRef = useRef(ghostAt);
  ghostAtRef.current = ghostAt;

  // ── actions ───────────────────────────────────────────────────────
  // R with a piece in hand: an eighth, or a quarter for a long piece (from
  // the quarter it is showing, so a 45° left over from a chair never sticks)
  const turnGhost = () => {
    setGhostRot((r) => (turnsInQuarters(activeGhostItem) ? quarterRot(r) : r) + rotStepFor(AREAS.YARD, activeGhostItem));
    sfx.turn();
  };
  const doPlace = async () => {
    if (!isOwner) { say(false, "You can only build in your own holding."); return; }
    if (busy || !ghost) return;
    if (ghost.state === "blocked") { say(false, ghost.reason || "It cannot go there."); return; }
    const at = ghostAt;
    setBusy(true);
    try {
      if (movingPropId) {
        const from = holding.props?.[movingPropId];
        const r = await moveProp(viewerUid, movingPropId, at.x, at.y, holding.props, effRot);
        if (r.ok) {
          sfx.place();
          if (from) undo.push({ type: "move", propId: movingPropId, x: from.x, y: from.y, rot: from.rot || 0 });
          setMoving(null); setAim(null); setPicked(movingPropId);
          say(true, "Moved.");
        } else say(false, r.message || "It would not go there.");
        return;
      }
      if (!ghostItemId) return;
      const item = ITEMS[ghostItemId];
      const on = ghost.state === "stack" ? ghost.on : null;
      const r = await placeProp(viewerUid, {
        itemId: ghostItemId, area: AREAS.YARD, x: at.x, y: at.y, rot: effRot, on,
      }, holding.props);
      if (!r.ok) { say(false, r.message || "It would not stand up."); return; }
      on ? sfx.stack() : sfx.place();
      undo.push({ type: "place", propId: r.propId, itemId: ghostItemId });
      say(true, on ? `${item.name} balanced on the ${ghost.onName}.` : `${item.name} placed.`);
      // Keep the ghost while you still have more of them — putting down
      // twelve slabs of path should not be twelve trips to the panel.
      // ⚑ and step the aim one tile on, so the next slab is not red on top
      // of the one you just laid.
      if (stockOf(stock, ghostItemId) <= 1) { setGhost(null); setAim(null); }
      else setAim(nextAim(at, facingMirror, holding.props, ghostItemId, AREAS.YARD, { floorOk: item?.walkOver ? null : yardFloorOk, rot: effRot }));
    } finally { setBusy(false); }
  };

  const doHarvest = async (propId) => {
    if (!isOwner) { say(false, "That is the host's to collect."); return; }
    if (busy) return;
    setBusy(true);
    try {
      const r = await harvest(viewerUid, propId);
      if (r.ok) {
        const at = resolvePlacement(holdingRef.current.props, propId);
        if (at) burst(at.x, at.y, r.paidUD);
        sfx.harvest(r.paidUD);
      }
      say(!!r.ok, r.ok ? (r.message || `+${UD}${fmt(r.paidUD)}`) : (r.message || "Not ready."));
    } finally { setBusy(false); }
  };

  const doHarvestAll = async () => {
    if (!isOwner || busy) return;
    setBusy(true);
    try {
      const before = holdingRef.current;
      const r = await harvestAll(viewerUid, before);
      for (const res of r.results || []) {
        if (!res.ok) continue;
        const at = resolvePlacement(before.props, res.propId);
        if (at && (before.props[res.propId]?.area || AREAS.YARD) === AREAS.YARD) burst(at.x, at.y, res.paidUD);
      }
      if (r.ok) sfx.harvest(r.total);
      say(!!r.ok, r.ok ? `+${UD}${fmt(r.total)} from ${r.count} ${r.count === 1 ? "grower" : "growers"}.`
                       : "Nothing is ready yet.");
    } finally { setBusy(false); }
  };

  const doTurn = async (propId) => {
    if (!isOwner) { say(false, "You can only move things in your own holding."); return; }
    if (busy || !propId) return;
    setBusy(true);
    try {
      const r = await rotateProp(viewerUid, propId, 1, holding.props);
      if (r && r.ok) {
        sfx.turn();
        // ⭐ a turn is a step Z can take back (E2E review: Z used to undo the
        // step BEFORE the turn — putting the chair back in the shed)
        if (r.from) undo.push({ type: "turn", propId, x: r.from.x, y: r.from.y, rot: r.from.rot });
      }
      else if (r && !r.ok) say(false, r.message || "It would not turn.");
    } catch { say(false, "It would not turn. Try again."); }
    finally { setBusy(false); }
  };

  const doPickUp = async (propId) => {
    if (!isOwner) { say(false, "You can only move things in your own holding."); return; }
    if (busy) return;
    setBusy(true);
    try {
      const r = await pickUpProp(viewerUid, propId, holding.props);
      if (r.ok) {
        sfx.pickup();
        undo.push({ type: "pickup", propId, itemId: r.itemId, x: r.x, y: r.y, rot: r.rot, on: r.on || null });
        if (pickedProp === propId) setPicked(null);
        say(true, r.orphaned ? `Back in the shed. ${r.orphaned} thing${r.orphaned > 1 ? "s" : ""} came off the top.` : "Back in the shed.");
      } else say(false, r.message || "It would not come up.");
    } finally { setBusy(false); }
  };

  // ⭐ UNDO — reverses the last place, pick-up or move. One level at a
  // time, through the same writers, so it can never do anything E, R and
  // PICK UP could not.
  const doUndo = async () => {
    if (!isOwner || busy || !undo.canUndo) return;
    const e = undo.pop();
    if (!e) return;
    setBusy(true);
    try {
      let r = { ok: false };
      if (e.type === "place")  r = await pickUpProp(viewerUid, e.propId, holdingRef.current.props);
      if (e.type === "pickup") {
        r = await placeProp(viewerUid, { itemId: e.itemId, area: AREAS.YARD, x: e.x, y: e.y, rot: e.rot, on: e.on || null }, holdingRef.current.props);
        // ⚠ it came back under a NEW id — every older step must follow it
        if (r.ok) undo.remap(e.propId, r.propId);
      }
      if (e.type === "move") r = await moveProp(viewerUid, e.propId, e.x, e.y, holdingRef.current.props, e.rot);
      // a turn is taken back by turning the other way (works stacked, too)
      if (e.type === "turn") r = await rotateProp(viewerUid, e.propId, -1, holdingRef.current.props);
      if (r.ok) { sfx.undo(); say(true, "Undone."); }
      // ⚠ a refused undo goes back on the stack — clear the spot and Z again
      else {
        const kept = undo.restore(e, r);
        say(false, `${r.message || "That could not be undone."}${kept ? " (Z again once it can.)" : ""}`);
      }
    } finally { setBusy(false); }
  };

  const doWear = async (itemId) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await wearItem(viewerUid, itemId);
      if (r.ok) sfx.ui();
      say(!!r.ok, r.ok ? `${ITEMS[itemId].name} settled over the holding.` : r.message);
    } finally { setBusy(false); }
  };

  const doCompanion = async (beingId) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await setCompanion(viewerUid, beingId, "beb");
      if (r.ok) sfx.ui();
      say(!!r.ok, r.ok ? (beingId ? `${companionCard(beingId)?.name} is with you.` : "Sent home.") : "It would not settle.");
    } finally { setBusy(false); }
  };

  // shop → yard in one tap: close the counter, open the shed, hold it
  const holdFromShop = (itemId) => {
    if (!ITEMS[itemId]?.areas?.includes(AREAS.YARD)) { say(true, `${ITEMS[itemId]?.name || "That"} waits indoors — ⚒ FURNISH in your Hall.`); setOverlay(null); return; }
    setOverlay(null); setBuildMode(true); setMoving(null); setAim(null);
    setGhost(itemId); setGhostRot(0);
  };

  const startMove = (propId) => {
    // a stacked piece is lifted off first — a green ghost that E then
    // refuses is the prompt lying (E2E review)
    if (holding.props?.[propId]?.on && movingPropId !== propId) { say(false, "Take it off the stack first — PICK UP, then put it down."); return; }
    setGhost(null); setAim(null);
    setGhostRot(holding.props?.[propId]?.rot || 0);
    setMoving((cur) => (cur === propId ? null : propId));
  };

  // ⭐ THE E ACTION, WRITTEN ONCE. Key and thumb button call this.
  // ⚑ ORDER (2026-09-15): a held piece wins over the porch. It used to
  // check the door first, so placing a lantern by the house walked you
  // inside and unmounted the screen.
  const doE = () => {
    if (overlay) return;
    if (activeGhostItem && buildMode) { doPlace(); return; }
    if (nearDoor) { sfx.door(); onEnterHall?.(); return; }
    if (activeProp) {
      const p = holding.props?.[activeProp];
      const item = p && ITEMS[p.itemId];
      if (item?.kind === KIND.GROWER) doHarvest(activeProp);
      else if (item?.kind === KIND.DOOR) { sfx.door(); onEnterHall?.(); }
      else if (buildMode) startMove(activeProp);
      return;
    }
    if (nearCompanion && holding.companion?.beingId) {
      petRef.current += 1;
      sfx.stack();
      say(true, `${companionCard(holding.companion.beingId)?.name || "It"} is glad you did that.`);
    }
  };

  // ── E / B / R / Z, once per press ─────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (overlay || e.repeat || isTypingTarget(e)) return;
      const k = e.key.toLowerCase();
      if (k === "e") doE();
      else if (k === "b" && isOwner) {
        // ⚠ the other setters are called HERE, not inside the updater —
        // an updater must be pure (React double-invokes it in StrictMode)
        const next = !buildMode;
        setBuildMode(next);
        if (!next) { setGhost(null); setMoving(null); setAim(null); }
        sfx.ui();
      }
      else if (k === "r" && buildMode) {
        if (activeGhostItem) turnGhost();
        else if (activeProp) doTurn(activeProp);
      }
      else if (k === "z" && buildMode && !e.ctrlKey && !e.metaKey) doUndo();
      else if (k === "z" && (e.ctrlKey || e.metaKey) && buildMode) { e.preventDefault(); doUndo(); }
      else if (k === "q" && buildMode && activeProp && !activeGhostItem) doPickUp(activeProp);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // ⚠ EVERY value the handlers close over is a dep — `ghostRot`, `stock`,
    // `movingPropId`, `ghostItemId`, `busy`, `ghost`, `aim` — or a stale
    // listener places the old piece at the old angle (see git history for
    // the two times this list was short).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay, nearDoor, activeProp, buildMode, activeGhostItem, holding.props, holding.companion,
      isOwner, ghostRot, effRot, stock, movingPropId, ghostItemId, busy, viewerUid, ghost, ghostAt, aim, undo.canUndo, nearCompanion]);

  // ── taps on the world, from the renderer ──────────────────────────
  // By REF, so Minerium3D's memo never sees a new function.
  const tapRef = useRef({});
  tapRef.current = {
    ground: (x, y) => {
      if (!isOwner || !buildMode || overlay) return;
      if (!activeGhostItem) { setPicked(null); return; }
      setAim({ x, y });
    },
    prop: (propId) => {
      if (!isOwner || overlay) return;
      if (buildMode && activeGhostItem && !movingPropId) {
        // tapping a piece while holding one: aim at it (to stack)
        const at = resolvePlacement(holding.props, propId);
        if (at) setAim({ x: at.x, y: at.y });
        return;
      }
      setPicked((cur) => (cur === propId ? null : propId));
      sfx.ui();
    },
  };

  // ── derived, cheap ────────────────────────────────────────────────
  const [ripeTick, setRipeTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setRipeTick((v) => v + 1), 5000);
    return () => clearInterval(t);
  }, []);

  // ⚠ `ripeTick` IS DECLARED ABOVE THIS LINE ON PURPOSE (the TDZ
  // ReferenceError of 2026-09-14 lives in git history).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pending = useMemo(() => pendingTotal(holding, serverNow()), [holding, ripeTick]);
  // the yard's own share, so the button can say when it is also sweeping the Hall
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pendingYard = useMemo(() => pendingTotal({ ...holding, props: Object.fromEntries(Object.entries(holding.props || {}).filter(([, p]) => (p.area || AREAS.YARD) === AREAS.YARD)) }, serverNow()), [holding, ripeTick]);
  const activeItem = activeProp ? ITEMS[holding.props?.[activeProp]?.itemId] : null;
  const activeGrowth = activeItem?.kind === KIND.GROWER
    ? growthOf(activeItem, holding.growers?.[activeProp], serverNow()) : null;

  const shed = useMemo(() => shedFor(stock, AREAS.YARD), [stock]);
  // ⭐ OW-34 · what is bought but belongs indoors, so it never reads as lost
  const indoorsCount = useMemo(() => shedFor(stock, AREAS.HALL)
    .filter((i) => !i.areas?.includes(AREAS.YARD)).reduce((a, i) => a + i.count, 0), [stock]);

  const promptTop = barH + 14;
  const mobile = isMobile();

  return (
    <div style={{ position: "fixed", inset: 0, overflow: "hidden", background: "#04030a", fontFamily: MONO }}>
      <Minerium3D
        holding={holding}
        posRef={posRef}
        facingRef={facingRef}
        ghostAtRef={ghostAtRef}
        tapRef={tapRef}
        bursts={bursts}
        gender={gender}
        title={isOwner ? "YOUR MINERIUM" : (ownerName ? `${ownerName.toUpperCase()}'S MINERIUM` : "A MINERIUM")}
        buildMode={buildMode}
        ghostItemId={activeGhostItem}
        ghostRot={effRot}
        ghostState={ghost?.state || "free"}
        selectedId={buildMode ? activeProp : null}
        movingPropId={movingPropId}
        petRef={petRef}
        companionPosRef={companionPosRef}
        othersRef={othersRef}
        rosterKey={rosterKey}
      />

      {/* ── THE BAR ───────────────────────────────────────────────── */}
      <div ref={barRef} style={{ ...panel, top: 12, left: 12, right: 12, padding: "9px 12px",
                    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button onClick={onLeave} style={btn(false)}>← PIER</button>
        <div style={{ fontSize: 12, letterSpacing: ".14em", color: GREEN, fontWeight: 700 }}>
          THE MINERIUM {ownerName && !isOwner ? `OF ${ownerName.toUpperCase()}` : ""}
        </div>
        <div style={{ flex: 1 }} />
        {isOwner ? (
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={cap}>DAWGS</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: UD_ROW.tint }}>{UD} {fmt(dawgs)}</span>
          </div>
        ) : (
          <div style={{ ...cap, color: UI.gold }}>
            {Object.keys(holding.props || {}).length === 0
              ? "VISITING · NOTHING BUILT HERE YET"
              : "VISITING · LOOK, DON'T TOUCH"}
          </div>
        )}
        {isOwner && pending > 0 && (
          <button onClick={doHarvestAll} disabled={busy} style={solidBtn(GREEN, busy)}>
            COLLECT ALL · {UD}{fmt(pending)}{pendingYard < pending ? " · incl. indoors" : ""}
          </button>
        )}
        {isOwner && <>
          <button onClick={() => { sfx.ui(); setOverlay("shop"); }} style={btn(overlay === "shop")}>BONNIE'S BASKET</button>
          <button onClick={() => { sfx.ui(); setOverlay("sky"); }} style={btn(overlay === "sky")}>SKY &amp; GROUND</button>
          <button onClick={() => { sfx.ui(); setOverlay("companion"); }} style={btn(overlay === "companion")}>COMPANION</button>
          <button onClick={() => { sfx.ui(); const n = !buildMode; setBuildMode(n); if (!n) { setGhost(null); setMoving(null); setAim(null); } }}
                  style={btn(buildMode, UI.gold)}>⚒ BUILD {buildMode ? "· ON" : ""}</button>
        </>}
      </div>

      {/* ── FIRST VISIT ───────────────────────────────────────────── */}
      {isOwner && !overlay && (
        <OnboardCard uid={viewerUid} room="yard" title="YOUR MINERIUM" lines={[
          ["WALK", mobile ? "the D-pad, bottom right" : "WASD or the arrow keys · drag to look around"],
          ["BUILD", "⚒ BUILD opens the shed · pick a piece, it floats ahead of you"],
          [mobile ? "TAP" : "CLICK", "a tile to aim the piece there · a piece to select it"],
          ["E", "puts a piece down · collects a ripe grower · goes through a door"],
          ["R · Q · Z", "turn · pick up · undo"],
          ["DAWGS ✹", "growers pay on a timer, up to an 8-hour barn. Bonnie's Basket takes them."],
        ]} />
      )}

      {/* ── THE PROMPT ────────────────────────────────────────────── */}
      {!overlay && (nearDoor || activeProp || activeGhostItem || nearCompanion) && (
        <div style={{ ...panel, left: "50%", transform: "translateX(-50%)",
                      // ⚠ on a phone the shed owns the lower-left; the prompt sits above it
                      ...(mobile ? { bottom: buildMode ? ABOVE_SHED_MOBILE : 168 } : { bottom: 74 }),
                      padding: "8px 14px", fontSize: 11.5, whiteSpace: mobile ? "normal" : "nowrap", maxWidth: "calc(100vw - 24px)" }}>
          {activeGhostItem && ghost ? (
            <span>
              <b style={{ color: GHOST_VERB[ghost.state].tint }}>
                {ghost.state === "blocked" ? GHOST_VERB.blocked.label
                  : `E · ${ghost.state === "stack" ? `BALANCE ON THE ${String(ghost.onName).toUpperCase()}` : movingPropId ? "SET IT DOWN" : "PUT IT DOWN"}`}
              </b>
              <span style={{ color: UI.dim }}>
                {ghost.state === "blocked" ? ` · ${ghost.reason}` : ` · R turns · ${mobile ? "tap a tile to aim" : "click a tile to aim"} · ESC drops it`}
              </span>
            </span>
          ) : nearDoor ? (
            <span><b style={{ color: UI.goldHi }}>E</b> · go inside to the Hall of Deeds</span>
          ) : activeGrowth && activeGrowth.ready > 0 && isOwner ? (
            <span><b style={{ color: GREEN }}>E</b> · collect {UD}{fmt(activeGrowth.ready)} from the {activeItem.name}</span>
          ) : activeGrowth && activeGrowth.ready > 0 ? (
            <span style={{ color: UI.dim }}>{activeItem.name} · ripe, and not yours to pick</span>
          ) : activeGrowth ? (
            <span style={{ color: UI.dim }}>
              {activeItem.name} · {activeGrowth.capped ? "the barn is full — collect it" : `next ${UD}${activeItem.grow.yieldUD} in ${Math.ceil(activeGrowth.msToNext / 60000)} min`}
              {activeGrowth.cycles > 0 && !activeGrowth.capped ? ` · ${UD}${fmt(activeGrowth.ready)} waiting` : ""}
            </span>
          ) : activeItem?.kind === KIND.DOOR ? (
            <span><b style={{ color: UI.goldHi }}>E</b> · through the {activeItem.name}</span>
          ) : activeItem && buildMode && isOwner ? (
            <span><b style={{ color: UI.goldHi }}>E</b> · move the {activeItem.name}
              <span style={{ color: UI.dim }}> · R turns · Q picks up</span></span>
          ) : activeItem ? (
            <span style={{ color: UI.dim }}>{activeItem?.name}</span>
          ) : nearCompanion && holding.companion?.beingId ? (
            <span><b style={{ color: UI.goldHi }}>E</b> · pet {companionCard(holding.companion.beingId)?.name}</span>
          ) : null}
        </div>
      )}

      {/* ── ⭐ THUMB CONTROLS (same functions the keys call) ───────── */}
      {mobile && !overlay && (nearDoor || activeProp || activeGhostItem || nearCompanion) && (
        <div style={{
          position: "fixed", zIndex: 620,
          left: 12, right: 190, bottom: 20,
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
        }}>
          <button onClick={doE} disabled={busy || (activeGhostItem && ghostBlocked)}
                  style={uiBtn(activeGhostItem ? GHOST_VERB[ghost?.state || "free"].tint : UI.ok, { on: true })}>
            {activeGhostItem ? GHOST_VERB[ghost?.state || "free"].label
              : nearDoor ? "GO INSIDE"
              : activeGrowth && activeGrowth.ready > 0 && isOwner ? `COLLECT ${UD}${fmt(activeGrowth.ready)}`
              : activeItem?.kind === KIND.DOOR ? "GO THROUGH"
              : buildMode && activeItem ? "MOVE"
              : !activeItem && nearCompanion ? "PET" : "…"}
          </button>
          {activeGhostItem && (
            <button onClick={turnGhost} style={uiBtn(UI.gold)}>TURN</button>
          )}
          {activeGhostItem && (
            <button onClick={() => { setGhost(null); setMoving(null); setAim(null); }} style={uiBtn(UI.gold)}>DROP IT</button>
          )}
          {!activeGhostItem && buildMode && activeItem && (
            <button onClick={() => doPickUp(activeProp)} disabled={busy} style={uiBtn(UI.err)}>PICK UP</button>
          )}
        </div>
      )}

      {/* ── FLASH — below the bar, whatever height it wrapped to ──── */}
      {flash && (
        <div style={{ ...panel, top: promptTop, left: "50%", transform: "translateX(-50%)",
                      padding: "8px 14px", fontSize: 11.5, maxWidth: "min(520px, calc(100vw - 24px))",
                      borderColor: flash.ok ? `${UI.ok}55` : `${UI.err}55`,
                      color: flash.ok ? UI.ok : UI.err, zIndex: 640 }}>
          {flash.text}
        </div>
      )}

      {/* ── BUILD PANEL — the shared shed ─────────────────────────── */}
      {isOwner && buildMode && !overlay && (
        <ShedPanel
          shed={shed}
          held={ghostItemId}
          busy={busy}
          canUndo={undo.canUndo}
          onUndo={doUndo}
          onPick={(id) => { setMoving(null); setAim(null); setGhostRot(0); setGhost(id); }}
          onClose={() => { setBuildMode(false); setGhost(null); setMoving(null); setAim(null); }}
          emptyLine={indoorsCount
            ? `Nothing for the yard. ${indoorsCount} piece${indoorsCount > 1 ? "s wait" : " waits"} indoors — ⚒ FURNISH in your Hall.`
            : "Nothing put by. Bonnie's Basket sells bundles — that is the cheap way to fill a yard."}
          footer={activeProp && holding.props?.[activeProp] && !activeGhostItem ? (
            <div style={{ borderTop: `1px solid ${UI.edge}`, padding: 9 }}>
              <div style={{ ...cap, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                <Thumb itemId={holding.props[activeProp].itemId} size={26} />
                {ITEMS[holding.props[activeProp].itemId]?.name?.toUpperCase()}
              </div>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                <button disabled={busy} onClick={() => doTurn(activeProp)} style={btn(false)}>TURN · R</button>
                <button onClick={() => startMove(activeProp)} style={btn(movingPropId === activeProp, UI.gold)}>MOVE · E</button>
                <button disabled={busy} onClick={() => doPickUp(activeProp)} style={btn(false, UI.err)}>PICK UP · Q</button>
              </div>
            </div>
          ) : indoorsCount && shed.length ? (
            <div style={{ borderTop: `1px solid ${UI.edge}`, padding: "7px 9px", fontSize: 10.5, color: UI.dim }}>
              + {indoorsCount} piece{indoorsCount > 1 ? "s wait" : " waits"} indoors — ⚒ FURNISH in your Hall.
            </div>
          ) : null}
        />
      )}

      {/* ── OVERLAYS ──────────────────────────────────────────────── */}
      {/* ⭐ ONE STORE (caP'n, 2026-09-25): this button is a shortcut to
          Bonnie's Bunni Basket, opened on its Minerium counter. */}
      {overlay === "shop" && (
        <PierMarketplace
          uid={viewerUid} playerName={viewerName} mark={profile?.mark}
          initialCounter="minerium" onHold={holdFromShop}
          onClose={() => setOverlay(null)}
        />
      )}
      {overlay === "sky" && (
        <Wardrobe
          owned={owned} holding={holding} busy={busy}
          onWear={doWear} onClose={() => setOverlay(null)}
        />
      )}
      {overlay === "companion" && (
        <CompanionPicker
          current={holding.companion?.beingId} uid={viewerUid} busy={busy}
          onPick={doCompanion} onClose={() => setOverlay(null)}
        />
      )}
      {mobile && <MobileDpad keysRef={keysRef} />}
    </div>
  );
}

// ⭐ MYSTIC ALLY'S COUNTER moved out (2026-09-25) — it is now Bonnie's Bunni
// Basket's Minerium counter, `BonnieMinerium.jsx`, one store with the Mark plates.
// ════════════════════════════════════════════════════════════════════
// THE WARDROBE — skies and grounds you own
// ════════════════════════════════════════════════════════════════════
// ⚠ Hoisted out of `Wardrobe` (2026-09-15): a component defined inside a
// render body is a NEW component type every render, so every row
// remounted on each `busy`/`holding` change.
function WardrobeRow({ list, worn, label, busy, owned, onWear }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ ...cap, marginBottom: 7 }}>{label}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 }}>
        {list.map((it) => {
          // a free thing is owned by everybody (same rule as wearItem) — or the
          // standard pier sky could never be gone back to from here
          const have = !!owned[it.id] || listPriceOf(it.id) === 0;
          const on = worn === it.id;
          return (
            <button key={it.id} disabled={!have || busy || on} onClick={() => onWear(it.id)}
              style={{ ...btn(on, SHELF_META[it.shelf].tint), textAlign: "left", padding: 10,
                       opacity: have ? 1 : 0.4, cursor: have && !on ? "pointer" : "default" }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>{it.name}</div>
              <div style={{ fontSize: 10.5, color: UI.dim, marginTop: 4, lineHeight: 1.5 }}>{it.blurb}</div>
              <div style={{ fontSize: 10, marginTop: 6, letterSpacing: ".14em",
                            color: on ? GREEN : have ? UI.dim : UI.faint }}>
                {on ? "WORN" : have ? "TAP TO WEAR" : `${UD}${fmt(priceOf(it.id))} AT BONNIE'S`}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Wardrobe({ owned, holding, busy, onWear, onClose }) {
  const skies   = Object.values(ITEMS).filter((i) => i.kind === KIND.SKY);
  const grounds = Object.values(ITEMS).filter((i) => i.kind === KIND.GROUND);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(2,3,1,.82)",
                                    display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ ...panel, position: "relative", width: "min(760px, 100%)", maxHeight: "86vh", overflowY: "auto", padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 13, letterSpacing: ".16em", color: GREEN, fontWeight: 700 }}>OVER THE HOLDING</div>
          <button onClick={onClose} style={btn(false)}>CLOSE</button>
        </div>
        <WardrobeRow list={skies}   worn={holding.sky}    label="THE SKY · worn over the pier too"    busy={busy} owned={owned} onWear={onWear} />
        <WardrobeRow list={grounds} worn={holding.ground} label="THE GROUND" busy={busy} owned={owned} onWear={onWear} />
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE COMPANION PICKER
// ────────────────────────────────────────────────────────────────────
// ⚠ EVERY BEING IN BOTH CANON KITS, and no NPC — the roster comes out
// of `mineriumCompanion.jsx`, which reads only `ieBeingModels.jsx` and
// `ieTerrorSpecies.jsx`. V'ael, Willie and the other NPCs in the Toonz
// folder are not cards and are not in either kit, so this list cannot
// offer one even by accident.
//
// ⚠ AND THE GATE IS OPEN, DELIBERATELY. `companionUnlocked()` returns
// true for everything right now — scaffolding first, so the seam is
// proven to work before the earning rules exist. It is ONE function
// when caP'n decides how a companion is earned.
// ════════════════════════════════════════════════════════════════════
function CompanionPicker({ current, uid, busy, onPick, onClose }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return COMPANIONS.filter((c) => !needle
      || c.name.toLowerCase().includes(needle)
      || c.ilk?.toLowerCase().includes(needle)
      || c.cls?.toLowerCase().includes(needle));
  }, [q]);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(2,3,1,.82)",
                                    display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ ...panel, position: "relative", width: "min(820px, 100%)", maxHeight: "86vh",
                    display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "13px 16px", borderBottom: `1px solid ${UI.edge}`,
                      display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, letterSpacing: ".16em", color: GREEN, fontWeight: 700 }}>YOUR COMPANION</div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search the vault…"
                 style={{ flex: 1, minWidth: 140, background: UI.panelSolid, border: `1px solid ${UI.edge}`,
                          borderRadius: 3, color: UI.text, fontFamily: MONO, fontSize: 12, padding: "6px 9px" }} />
          {current && <button onClick={() => onPick(null)} disabled={busy} style={btn(false, UI.err)}>SEND HOME</button>}
          <button onClick={onClose} style={btn(false)}>CLOSE</button>
        </div>

        <div style={{ padding: "7px 16px", fontSize: 10.5, color: UI.dim }}>
          {/* ⚠ This said "the Toonz vault and the core cast" while
              SPECIES_KIT is false, so it named a vault of 37 species the
              picker cannot offer. Say what is actually on the list. */}
          {list.length} beings · the core cast, cards only
        </div>

        <div style={{ overflowY: "auto", padding: "4px 16px 16px",
                      display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(196px, 1fr))", gap: 8 }}>
          {list.map((c) => {
            const on = current === c.id;
            const unlocked = companionUnlocked(uid, c.id);
            return (
              <button key={c.id} disabled={busy || !unlocked} onClick={() => onPick(c.id)}
                style={{ ...btn(on, c.pal?.main || GREEN), textAlign: "left", padding: 10,
                         opacity: unlocked ? 1 : 0.35 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <span style={{ width: 30, height: 30, borderRadius: "50%", flex: "none",
                                 background: `radial-gradient(circle at 40% 35%, ${c.pal?.glow || GREEN}, ${c.pal?.main || GREEN} 60%, #000 100%)`,
                                 boxShadow: `0 0 10px ${c.pal?.glow}` }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: UI.text }}>{c.name}</span>
                </div>
                <div style={{ fontSize: 10, letterSpacing: ".12em", color: UI.dim, marginTop: 5 }}>
                  {c.ilk} · {c.cls}
                </div>
                {c.flavor && (
                  <div style={{ fontSize: 10.5, color: UI.dim, marginTop: 6, lineHeight: 1.5,
                                display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {c.flavor}
                  </div>
                )}
                {on && <div style={{ fontSize: 10, letterSpacing: ".16em", color: GREEN, marginTop: 6 }}>WITH YOU</div>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
